import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PackageSearch, MapPin, ShieldCheck, Users, Building2, Headphones, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";
import { APP_URL } from "@/i18n/translations";

const ICONS = [PackageSearch, MapPin, ShieldCheck, Users, Building2, Headphones];

export default function Services() {
  const { t } = useLang();
  const s = t.services;

  return (
    <div className="relative z-10 bg-[#050505]" data-testid="services-page">
      {/* Intro */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-40 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7 }}
          className="text-xs tracking-[0.25em] text-[#FF3B30] font-medium mb-8"
          data-testid="services-eyebrow"
        >
          {s.eyebrow}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black tracking-tighter text-5xl sm:text-7xl lg:text-8xl leading-[0.9] text-white/95 max-w-4xl"
        >
          {s.title}
        </motion.h1>
        <Reveal delay={0.2}>
          <p className="mt-8 text-white/55 text-lg max-w-xl leading-relaxed">{s.intro}</p>
        </Reveal>
      </section>

      {/* Bento grid */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-24 md:pb-32">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-3xl overflow-hidden">
          {s.items.map((item, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal
                key={item.title}
                delay={(i % 3) * 0.08}
                className="group bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#111] transition-colors duration-500"
                data-testid={`service-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/20 flex items-center justify-center text-[#FF3B30] group-hover:bg-[#FF3B30] group-hover:text-white transition-all duration-500">
                  <Icon size={22} />
                </div>
                <h3 className="mt-7 font-display font-bold text-2xl text-white/95">{item.title}</h3>
                <p className="mt-3 text-white/50 leading-relaxed">{item.desc}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-32 md:pb-44">
        <Reveal className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0c0c0c] p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-[#FF3B30]/15 blur-3xl" />
          <h2 className="relative font-display font-black tracking-tighter text-3xl sm:text-5xl text-white/95 max-w-lg leading-[0.95]">
            {t.home.cta_band_title}
          </h2>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta"
            className="relative group shrink-0 inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5247] text-white text-lg font-black px-9 py-5 rounded-full transition-all duration-300 hover:scale-[1.03] shadow-[0_0_40px_rgba(255,59,48,0.4)]"
          >
            {t.nav.cta}
            <ArrowUpRight size={24} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </Reveal>
      </section>
    </div>
  );
}
